import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TIER_FEATURES } from '../data/onboardingData';
import './WelcomeModal.css';

/**
 * WelcomeModal — celebratory overlay shown once after a plan upgrade.
 *
 * Props:
 *   tier     — 'pro' | 'premium'
 *   userName — first name string
 *   onClose  — callback; removes show_welcome_modal flag and hides modal
 */
function WelcomeModal({ tier, userName, onClose }) {
  const navigate = useNavigate();

  // Prevent body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  if (!tier || (tier !== 'pro' && tier !== 'premium')) return null;

  const isPro = tier === 'pro';
  const features = TIER_FEATURES[tier] || [];

  const ctaPath = isPro ? '/matched-jobs' : '/cv-optimise';
  const ctaLabel = 'Explore Features';

  const handleCTA = () => {
    onClose();
    navigate(ctaPath);
  };

  // Generate confetti particles (30 total)
  const particles = Array.from({ length: 30 }, (_, i) => i);

  return (
    <div className="welcome-modal-overlay" onClick={onClose}>
      {/* Confetti particles */}
      <div className="welcome-modal-confetti" aria-hidden="true">
        {particles.map(i => (
          <div
            key={i}
            className="confetti-particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 1.5}s`,
              animationDuration: `${1.5 + Math.random() * 2}s`,
              background: isPro
                ? ['#f59e0b', '#fbbf24', '#fde68a', '#fff', '#d97706'][i % 5]
                : ['#8b5cf6', '#a78bfa', '#ddd6fe', '#fff', '#6d28d9'][i % 5],
            }}
          />
        ))}
      </div>

      <div
        className={`welcome-modal welcome-modal-${tier}`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-modal-title"
      >
        {/* Close button */}
        <button className="welcome-modal-close" onClick={onClose} aria-label="Close">
          <i className="fas fa-times"></i>
        </button>

        {/* Badge */}
        <div className="welcome-modal-badge">
          <i className={`fas ${isPro ? 'fa-crown' : 'fa-gem'}`}></i>
        </div>

        {/* Greeting */}
        <h2 id="welcome-modal-title" className="welcome-modal-title">
          Welcome to {isPro ? 'Pro' : 'Premium'}, {userName}! 🎉
        </h2>
        <p className="welcome-modal-subtitle">
          You've unlocked {features.length} powerful features to supercharge your job search.
        </p>

        {/* Feature list */}
        <ul className="welcome-modal-features">
          {features.map((f, i) => (
            <li key={i} className="welcome-modal-feature-item">
              <span className="welcome-modal-feature-icon">
                <i className={`fas ${f.icon}`}></i>
              </span>
              <span>{f.text}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <button className="welcome-modal-cta" onClick={handleCTA}>
          {ctaLabel} <i className="fas fa-arrow-right"></i>
        </button>
      </div>
    </div>
  );
}

export default WelcomeModal;
